/**
 * LA SECCION A PANTALLA COMPLETA
 * =============================================================================
 * Es el patrón de la portada, repetido en todas las secciones: la fotografía
 * ocupa TODA la pantalla, con un velo oscuro encima, y el texto va sobre ella.
 *
 * -----------------------------------------------------------------------------
 * POR QUÉ ASÍ
 * -----------------------------------------------------------------------------
 * La portada funciona porque la fotografía llena y el texto se apoya encima. Es lo
 * que le da la presencia de un cartel. En las secciones de contenido el texto va
 * sobre la fotografía igual, en una columna a la izquierda, porque el velo se
 * oscurece más de ese lado y el blanco se lee sin esfuerzo.
 *
 * -----------------------------------------------------------------------------
 * LAS MEDIDAS
 * -----------------------------------------------------------------------------
 *   · La sección mide 100vh como mínimo: ocupa la pantalla entera.
 *   · El texto se centra en el alto y va en una columna que no pasa de 720 px, para
 *     que en pantallas anchas no se extienda de lado a lado y cueste leerlo.
 *   · El contenido extra —la lista, las tarjetas, los botones— va debajo del texto,
 *     dentro de esa misma columna.
 *
 * En el teléfono la sección mide la pantalla igual, pero el velo se oscurece de
 * arriba abajo en lugar de lado a lado, porque ahí el texto ocupa el ancho completo.
 */

import type { ReactNode } from 'react';
import { Revelar } from './Piezas';
import { Icono } from './Iconos';

interface Props {
  /** El identificador de la sección, para las anclas del menú. */
  id: string;
  /** La fotografía. */
  foto: string;
  /** El texto alternativo, para quien no puede ver la imagen. */
  alt: string;
  /** El antetítulo, en mayúsculas pequeñas. */
  etiqueta?: string;
  /** El titular. Puede llevar marcado dentro. */
  titulo: ReactNode;
  /** El párrafo. */
  texto?: string;
  /**
   * El punto de la fotografía que se quiere que se vea. Va en porcentaje del ancho
   * y del alto, igual que `object-position`.
   */
  encuadre?: string;
  /** El encuadre en el teléfono, que casi siempre conviene subir. */
  encuadreMovil?: string;
  /** Si el titular es más pequeño, para los que son muy largos. */
  tituloMedio?: boolean;
  /** Lo que va debajo: botones, listas, tarjetas. */
  children?: ReactNode;
  /** Si el contenido se alinea al centro en lugar de a la izquierda. */
  centrado?: boolean;
  /** La sección de abajo de la página, que lleva la flecha de bajar. */
  conFlecha?: boolean;
  /** El texto de la flecha. */
  textoFlecha?: string;
}

export function PantallaCompleta({
  id,
  foto,
  alt,
  etiqueta,
  titulo,
  texto,
  encuadre = '50% 30%',
  encuadreMovil,
  tituloMedio = false,
  children,
  centrado = false,
  conFlecha = false,
  textoFlecha,
}: Props) {
  return (
    <section
      id={id}
      className={'pantalla' + (centrado ? ' pantalla--centrada' : '')}
      /* Las dos variables las lee el CSS: el encuadre en escritorio y en teléfono.
         Van aquí y no en el CSS porque cada fotografía tiene el sujeto en un sitio
         distinto. */
      style={
        {
          '--encuadre': encuadre,
          '--encuadre-movil': encuadreMovil ?? encuadre,
        } as React.CSSProperties
      }
    >
      {/* La fotografía, en su propia capa para poder encuadrarla sin tocar el
          contenido. */}
      <div className="pantalla__fondo" aria-hidden="true">
        <img src={foto} alt={alt} loading="lazy" decoding="async" />
      </div>

      {/* El velo. Sin él, el texto blanco no se leería sobre una fotografía clara. */}
      <div className="pantalla__velo" aria-hidden="true" />

      <div className="caja pantalla__contenido">
        <div className="pantalla__textos">
          {etiqueta && (
            <Revelar>
              <p className="antetitulo">{etiqueta}</p>
            </Revelar>
          )}

          <Revelar retardo={70}>
            <h2
              className={
                'titulo-seccion titulo-seccion--pantalla' +
                (tituloMedio ? ' titulo-seccion--medio' : '')
              }
            >
              {titulo}
            </h2>
          </Revelar>

          {texto && (
            <Revelar retardo={140}>
              <p className="pantalla__parrafo">{texto}</p>
            </Revelar>
          )}

          {children && <Revelar retardo={200}>{children}</Revelar>}

          {conFlecha && (
            <div className="pantalla__desliza" aria-hidden="true">
              <span>{textoFlecha}</span>
              <Icono nombre="desliza" tam={22} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
